import { CATEGORIES, DNS_PRESETS, GROUPS, ITEM_BY_ID, MIRRORS, REGIONS, type GroupKind, type RegionKey, type RuleItem } from '../shared/catalog.ts';
import {
  defaultConfig,
  defaultTarget,
  MAX_UPSTREAMS,
  targetOf,
  validateConfig,
  validateUpstreams,
  ValidationError,
  type Outbound,
  type RuleTarget,
  type UserConfig,
} from '../shared/config.ts';
import { airportGroupName, generateConfig, ownGroupName, planServiceGroups, regionAutoName, toYaml } from '../shared/generate.ts';
import type { NodeStats } from '../shared/nodes.ts';
import {
  CUSTOM_BY_ID,
  CUSTOM_ENTRIES,
  customDomains,
  effectiveOrder,
  entryLabel,
  findConflicts,
  PINNED_TAIL,
  recommendedOrder,
  type CustomKind,
} from '../shared/order.ts';
import { $, checkbox, compact, h, select, toast } from './dom.ts';
import { runTest, type TestResult } from './tester.ts';

// ---------- 本地存储 ----------

interface SavedSub {
  id: string;
  token: string;
  name: string;
}

const LS_KEY = 'csb.subs';
const ADMIN_KEY = 'csb.admin';

function lsGet(storage: 'local' | 'session', k: string): string | null {
  try {
    return (storage === 'local' ? localStorage : sessionStorage).getItem(k);
  } catch {
    return null;
  }
}

function loadSaved(): SavedSub[] {
  try {
    const v = JSON.parse(lsGet('local', LS_KEY) ?? '[]');
    return Array.isArray(v) ? v.filter((s) => s && typeof s.id === 'string' && typeof s.token === 'string') : [];
  } catch {
    return [];
  }
}

function saveSaved(list: SavedSub[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch {
    /* 隐私模式等场景写不进去,忽略 */
  }
}

const upsertSaved = (s: SavedSub) => saveSaved([s, ...loadSaved().filter((x) => x.id !== s.id)]);

function storeAdmin(token: string | null, remember = false) {
  try {
    sessionStorage.removeItem(ADMIN_KEY);
    localStorage.removeItem(ADMIN_KEY);
    if (token) (remember ? localStorage : sessionStorage).setItem(ADMIN_KEY, token);
  } catch {
    /* 存不进去就只在内存里保留 */
  }
}

// ---------- 状态 ----------

interface ProbeResult {
  name: string;
  ok: boolean;
  kind: 'inline' | 'provider';
  reason?: string;
  stats?: NodeStats;
}

let cfg: UserConfig = defaultConfig();
let current: SavedSub | null = null;
let step = 0;
let adminToken: string | null = lsGet('session', ADMIN_KEY) ?? lsGet('local', ADMIN_KEY);
let probe: { key: string; results: ProbeResult[] } | null = null;
let probing = false;
let ruleFilter = '';
let customEditor: CustomKind | null = null;
let testInput = '';
let testState: { running: boolean; progress?: string; result?: TestResult; error?: string } = { running: false };

const multi = () => cfg.upstreams.length > 1;
const probeKey = () => JSON.stringify([cfg.upstreams.map((u) => u.url.trim()), cfg.ua]);
const probeValid = () => (probe && probe.key === probeKey() ? probe : null);

function outboundOptions(): [Outbound, string][] {
  return [
    ['proxy', '🚀 节点选择'],
    ['auto', '♻️ 自动选择'],
    ...REGIONS.filter((r) => cfg.regions.includes(r.key)).flatMap((r): [Outbound, string][] =>
      cfg.regionType === 'both' ? [[r.key, `${r.name}(手动)`], [`${r.key}:auto`, regionAutoName(r)]] : [[r.key, r.name]],
    ),
    ...(multi() ? cfg.upstreams.map((u, i): [Outbound, string] => [`air:${i}`, airportGroupName({ ...u, name: u.name || `机场${i + 1}` })]) : []),
    ['direct', '直连 DIRECT'],
    ['reject', '拦截 REJECT'],
  ];
}

const groupName = (item: RuleItem) => GROUPS.find((g) => g.key === item.group)!.name;

function targetOptions(item: RuleItem): [RuleTarget, string][] {
  const def = defaultTarget(item);
  const mark = (t: RuleTarget, label: string) => (t === def ? `${label}(推荐)` : label);
  const opts: [RuleTarget, string][] = [];
  if (item.group !== 'proxy') opts.push(['cat', mark('cat', groupName(item))]);
  opts.push(['own', `独立分组 ${ownGroupName(item)}`]);
  for (const [o, label] of outboundOptions()) opts.push([o, mark(o, label)]);
  return opts;
}

/** 地区或机场被移除后,清掉指向它们的去向设置;非 both 模式下「地区 自动」并入地区组 */
function pruneRefs() {
  if (cfg.regionType !== 'both') {
    const merge = <T extends string>(rec: Record<string, T>) =>
      Object.fromEntries(Object.entries(rec).map(([k, v]) => [k, (v.endsWith(':auto') ? v.slice(0, -5) : v) as T]));
    cfg.targets = merge(cfg.targets);
    cfg.groupDefaults = merge(cfg.groupDefaults);
  }
  const valid = new Set(outboundOptions().map(([o]) => o as string));
  cfg.targets = Object.fromEntries(Object.entries(cfg.targets).filter(([, v]) => v === 'cat' || v === 'own' || valid.has(v)));
  cfg.groupDefaults = Object.fromEntries(Object.entries(cfg.groupDefaults).filter(([, v]) => valid.has(v)));
}

/** 删除第 k 个机场时,air:j 的引用整体前移 */
function shiftAirRefs(k: number) {
  const remap = (v: string) => {
    if (!v.startsWith('air:')) return v;
    const j = Number(v.slice(4));
    return j === k ? null : j > k ? `air:${j - 1}` : v;
  };
  const apply = <T extends string>(rec: Record<string, T>) =>
    Object.fromEntries(
      Object.entries(rec).flatMap(([key, v]) => {
        const r = remap(v);
        return r === null ? [] : [[key, r as T]];
      }),
    );
  cfg.targets = apply(cfg.targets);
  cfg.groupDefaults = apply(cfg.groupDefaults);
}

function draftConfig(): UserConfig {
  return { ...cfg, upstreams: cfg.upstreams.map((u, i) => ({ name: u.name.trim() || `机场${i + 1}`, url: u.url.trim() || `https://example.com/airport-${i + 1}` })) };
}

// ---------- 步骤框架 ----------

interface Step {
  title: string;
  question: string;
  status: () => string;
  render: () => HTMLElement;
}

const STEPS: Step[] = [
  { title: '节点来源', question: '节点从哪里来?', status: () => `${cfg.upstreams.filter((u) => u.url.trim()).length} 个机场`, render: renderSourceStep },
  { title: '选规则', question: '哪些流量需要特殊处理?', status: () => `已选 ${cfg.rules.length} 项`, render: renderRulesStep },
  {
    title: '顺序与去向',
    question: '按什么顺序匹配,匹配后走哪里?',
    status: () => {
      const order = effectiveOrder(cfg);
      const n = findConflicts(cfg, order).length;
      return n ? `⚠ ${n} 处提示` : `${order.length} 条规则`;
    },
    render: renderOrderStep,
  },
  { title: '策略组', question: '客户端里有哪些策略组?', status: () => `${planServiceGroups(cfg).length} 个服务分组`, render: renderGroupsStep },
  { title: '完成与测试', question: '生成订阅,并验证流量去向', status: () => (current ? '已保存' : '未生成'), render: renderFinishStep },
];

function renderStepper() {
  $('#stepper').replaceChildren(
    ...STEPS.map((s, i) =>
      h(
        'button',
        { type: 'button', class: `step${i === step ? ' active' : ''}${i < step ? ' done' : ''}`, onclick: () => goto(i), 'aria-current': i === step ? 'step' : undefined },
        h('span', { class: 'step-no' }, String(i + 1)),
        h('span', { class: 'step-text' }, h('b', {}, s.title), h('small', {}, s.status())),
      ),
    ),
  );
}

function renderBar() {
  const last = step === STEPS.length - 1;
  $('#barInner').replaceChildren(
    ...compact([
      h('span', { class: 'muted bar-status' }, current ? `正在编辑:${current.name}` : '新建订阅'),
      h('span', { class: 'spacer' }),
      step > 0 ? h('button', { type: 'button', class: 'ghost', onclick: () => goto(step - 1) }, '上一步') : null,
      !last ? h('button', { type: 'button', class: current ? 'ghost' : undefined, onclick: () => goto(step + 1) }, '下一步') : null,
      current || last ? h('button', { type: 'button', onclick: () => void save(false) }, current ? '保存修改' : '生成订阅链接') : null,
    ]),
  );
}

function renderPanel() {
  const s = STEPS[step];
  $('#panel').replaceChildren(
    h('div', { class: 'panel-head' }, h('span', { class: 'panel-step' }, `第 ${step + 1} 步 / 共 ${STEPS.length} 步`), h('h2', {}, s.question)),
    s.render(),
  );
}

function render() {
  renderStepper();
  renderPanel();
  renderBar();
  renderSaved();
}

/** 只刷新步骤条和底栏(输入过程中使用,避免重绘面板丢失焦点) */
function refreshChrome() {
  renderStepper();
  renderBar();
}

function goto(i: number) {
  if (step === 0 && i > 0) {
    try {
      validateUpstreams(cfg.upstreams.map((u) => ({ name: u.name.trim(), url: u.url.trim() })));
    } catch (e) {
      toast(e instanceof ValidationError ? e.message : '请先填写机场订阅', 'err');
      return;
    }
  }
  step = Math.max(0, Math.min(STEPS.length - 1, i));
  render();
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

// ---------- ① 节点来源 ----------

function renderSourceStep(): HTMLElement {
  const rows = cfg.upstreams.map((u, i) =>
    h(
      'div',
      { class: 'up-row' },
      h('input', {
        class: 'up-name',
        value: u.name,
        maxLength: 16,
        placeholder: `机场${i + 1}`,
        'aria-label': '机场名称',
        oninput: (e: Event) => {
          u.name = (e.target as HTMLInputElement).value;
        },
      }),
      h('input', {
        class: 'up-url',
        type: 'url',
        value: u.url,
        placeholder: 'https://airport.example.com/api/v1/client/subscribe?token=...',
        autocomplete: 'off',
        spellcheck: false,
        'aria-label': '订阅地址',
        oninput: (e: Event) => {
          u.url = (e.target as HTMLInputElement).value;
          refreshChrome();
        },
      }),
      cfg.upstreams.length > 1
        ? h(
            'button',
            {
              type: 'button',
              class: 'ghost small',
              onclick: () => {
                cfg.upstreams.splice(i, 1);
                shiftAirRefs(i);
                pruneRefs();
                render();
              },
            },
            '移除',
          )
        : null,
    ),
  );

  const p = probeValid();
  const probeBox = probing
    ? h('div', { class: 'probe muted' }, '正在检测订阅…')
    : p
      ? h('div', { class: 'probe' }, ...p.results.map(renderProbeResult))
      : probe
        ? h('div', { class: 'probe muted' }, '订阅已修改,请重新检测。')
        : null;

  return h(
    'div',
    {},
    h('p', { class: 'muted' }, '填入一个或多个机场的订阅地址。多个订阅会合并节点,节点名自动加「机场名 | 」前缀,并为每个机场生成 ✈️ 分组。'),
    h('div', { class: 'upstreams' }, ...rows),
    h(
      'div',
      { class: 'btnrow' },
      cfg.upstreams.length < MAX_UPSTREAMS
        ? h(
            'button',
            {
              type: 'button',
              class: 'ghost',
              onclick: () => {
                cfg.upstreams.push({ name: `机场${cfg.upstreams.length + 1}`, url: '' });
                render();
              },
            },
            '+ 添加机场',
          )
        : null,
      h('span', { class: 'spacer' }),
      h('button', { type: 'button', disabled: probing, onclick: () => void runProbe() }, '检测订阅'),
    ),
    probeBox,
    h('p', { class: 'muted small-note' }, '检测会拉取订阅并统计节点的地区分布,结果用于第 4 步预选地区;不会保存任何内容。'),
  );
}

function regionSummary(stats: NodeStats): string {
  const parts = REGIONS.filter((r) => stats.regions[r.key]).map((r) => `${r.name.split(' ')[1]} ${stats.regions[r.key]}`);
  if (stats.unmatched) parts.push(`未识别 ${stats.unmatched}`);
  return parts.join(' · ');
}

function renderProbeResult(r: ProbeResult): HTMLElement {
  if (!r.ok || !r.stats) {
    return h('div', { class: 'probe-row warn' }, h('b', {}, `⚠ ${r.name}`), h('span', {}, `由客户端拉取:${r.reason ?? '无法解析'}`));
  }
  return h('div', { class: 'probe-row ok' }, h('b', {}, `✓ ${r.name}`), h('span', {}, `${r.stats.total} 个节点`), h('span', { class: 'muted' }, regionSummary(r.stats)));
}

async function runProbe() {
  let ups;
  try {
    ups = validateUpstreams(cfg.upstreams.map((u) => ({ name: u.name.trim(), url: u.url.trim() })));
  } catch (e) {
    toast(e instanceof ValidationError ? e.message : '订阅地址有误', 'err');
    return;
  }
  probing = true;
  renderPanel();
  try {
    const data = await api('/api/probe', { method: 'POST', body: JSON.stringify({ upstreams: ups, ua: cfg.ua }) });
    probe = { key: probeKey(), results: data.results as ProbeResult[] };
  } catch (e) {
    toast(e instanceof Error ? e.message : '检测失败', 'err');
  } finally {
    probing = false;
    if (step === 0) renderPanel();
  }
}

// ---------- ② 选规则 ----------

function renderRulesStep(): HTMLElement {
  const list = h('div', { class: 'rule-lib' });
  const fill = () => list.replaceChildren(...renderRuleLibrary(fill));
  fill();
  const preset = (label: string, ids: () => string[]) =>
    h(
      'button',
      {
        type: 'button',
        class: 'ghost small',
        onclick: () => {
          cfg.rules = ids();
          fill();
          refreshChrome();
        },
      },
      label,
    );
  return h(
    'div',
    {},
    h('p', { class: 'muted' }, '勾选需要单独处理的规则即可,匹配顺序和去向在下一步调整。悬停可查看对应的 rule-set。'),
    h(
      'div',
      { class: 'toolbar' },
      h('input', {
        type: 'search',
        class: 'search',
        placeholder: '搜索规则,如 netflix、游戏、AI',
        value: ruleFilter,
        oninput: (e: Event) => {
          ruleFilter = (e.target as HTMLInputElement).value;
          fill();
        },
      }),
      preset('推荐', () => defaultConfig().rules),
      preset('极简', () => ['ads', 'private', 'ai-all', 'not-cn', 'cn-site', 'cn-ip']),
      preset('常用全选', () => CATEGORIES.flatMap((c) => c.items.filter((i) => !['gfw', 'social-all', 'games-all', 'dev-all', 'games-cn'].includes(i.id)).map((i) => i.id))),
    ),
    list,
  );
}

function renderRuleLibrary(refill: () => void): HTMLElement[] {
  const q = ruleFilter.trim().toLowerCase();
  const matches = (it: RuleItem, catLabel: string) =>
    !q || [it.label, it.desc ?? '', catLabel, ...it.sets.map((s) => s.name)].some((t) => t.toLowerCase().includes(q));
  const cats = CATEGORIES.map((cat) => ({ cat, items: cat.items.filter((it) => matches(it, cat.label)) })).filter((c) => c.items.length);
  if (!cats.length) return [h('p', { class: 'muted' }, '没有匹配的规则')];
  return cats.map(({ cat, items }) => {
    const ids = items.map((i) => i.id);
    const all = ids.every((id) => cfg.rules.includes(id));
    return h(
      'div',
      { class: 'cat' },
      h(
        'div',
        { class: 'cat-head' },
        h('h3', {}, cat.label),
        cat.hint ? h('span', { class: 'muted' }, cat.hint) : null,
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'link',
            onclick: () => {
              cfg.rules = all ? cfg.rules.filter((x) => !ids.includes(x)) : [...new Set([...cfg.rules, ...ids])];
              refill();
              refreshChrome();
            },
          },
          all ? '清空' : '全选',
        ),
      ),
      h(
        'div',
        { class: 'chips' },
        ...items.map((it) =>
          h(
            'label',
            { class: 'chip', title: it.sets.map((s) => `${s.type}:${s.name}`).join('  ') },
            h('input', {
              type: 'checkbox',
              checked: cfg.rules.includes(it.id),
              onchange: (e: Event) => {
                const on = (e.target as HTMLInputElement).checked;
                cfg.rules = on ? [...new Set([...cfg.rules, it.id])] : cfg.rules.filter((x) => x !== it.id);
                refreshChrome();
              },
            }),
            h('span', { class: 'chip-label' }, it.label),
            it.desc ? h('small', {}, it.desc) : null,
          ),
        ),
      ),
    );
  });
}

// ---------- ③ 顺序与去向 ----------

const movableOrder = () => effectiveOrder(cfg).filter((id) => !PINNED_TAIL.includes(id));

function moveEntry(from: number, to: number) {
  const order = movableOrder();
  if (to < 0 || to >= order.length || from === to) return;
  const [id] = order.splice(from, 1);
  order.splice(to, 0, id);
  cfg.order = order;
  renderPanel();
  refreshChrome();
}

function renderOrderStep(): HTMLElement {
  const order = effectiveOrder(cfg);
  const conflicts = new Map(findConflicts(cfg, order).map((c) => [c.id, c]));
  const movable = movableOrder();
  let dragFrom = -1;

  const rows = order.map((id, i) => {
    const pinned = PINNED_TAIL.includes(id);
    const custom = CUSTOM_BY_ID.get(id);
    const item = ITEM_BY_ID.get(id);
    const conflict = conflicts.get(id);

    const info = custom
      ? h('div', { class: 'ord-info' }, h('b', {}, custom.label), h('small', {}, customDomains(cfg, custom.kind).join('、')))
      : h('div', { class: 'ord-info', title: item!.sets.map((s) => `${s.type}:${s.name}`).join('  ') }, h('b', {}, item!.label), item!.desc ? h('small', {}, item!.desc) : null);

    const target = custom
      ? h('div', { class: 'ord-target' }, h('span', { class: 'badge' }, custom.target), h('button', { type: 'button', class: 'link', onclick: () => openCustomEditor(custom.kind) }, '编辑'))
      : h(
          'div',
          { class: 'ord-target' },
          select<RuleTarget>(
            targetOf(cfg, item!),
            targetOptions(item!),
            (v) => {
              if (v === defaultTarget(item!)) delete cfg.targets[item!.id];
              else cfg.targets[item!.id] = v;
              renderPanel();
              refreshChrome();
            },
            `rule-target${targetOf(cfg, item!) === defaultTarget(item!) ? '' : ' changed'}`,
          ),
        );

    const row = h(
      'li',
      { class: `ord-row${pinned ? ' pinned' : ''}${conflict ? ' has-warn' : ''}`, draggable: !pinned },
      pinned ? h('span', { class: 'ord-handle', title: '固定在末尾' }, '🔒') : h('span', { class: 'ord-handle', title: '拖拽排序' }, '⋮⋮'),
      h('span', { class: 'ord-no' }, String(i + 1)),
      info,
      target,
      pinned
        ? h('span', { class: 'ord-arrows' })
        : h(
            'span',
            { class: 'ord-arrows' },
            h('button', { type: 'button', class: 'icon', disabled: i === 0, 'aria-label': '上移', onclick: () => moveEntry(i, i - 1) }, '↑'),
            h('button', { type: 'button', class: 'icon', disabled: i === movable.length - 1, 'aria-label': '下移', onclick: () => moveEntry(i, i + 1) }, '↓'),
          ),
      conflict
        ? h('div', { class: 'ord-warn' }, `⚠ 排在第 ${conflict.byIndex + 1} 条「${entryLabel(conflict.by)}」之后,部分域名会被它提前匹配,不会走这里的去向。可把本条移到它上方。`)
        : null,
      pinned ? h('div', { class: 'ord-note muted' }, '会触发 DNS 解析,固定在末尾,避免前面的规则被拖慢') : null,
    );
    if (!pinned) {
      row.addEventListener('dragstart', (e) => {
        dragFrom = i;
        row.classList.add('dragging');
        e.dataTransfer?.setData('text/plain', String(i));
      });
      row.addEventListener('dragend', () => row.classList.remove('dragging'));
      row.addEventListener('dragover', (e) => {
        e.preventDefault();
        row.classList.add('drop-target');
      });
      row.addEventListener('dragleave', () => row.classList.remove('drop-target'));
      row.addEventListener('drop', (e) => {
        e.preventDefault();
        row.classList.remove('drop-target');
        if (dragFrom >= 0) moveEntry(dragFrom, i);
      });
    }
    return row;
  });

  rows.push(
    h(
      'li',
      { class: 'ord-row pinned' },
      h('span', { class: 'ord-handle' }, '🔒'),
      h('span', { class: 'ord-no' }, '—'),
      h('div', { class: 'ord-info' }, h('b', {}, 'MATCH 未匹配流量'), h('small', {}, '以上都没命中时走这里')),
      h(
        'div',
        { class: 'ord-target' },
        select(cfg.final, [['proxy', '🐟 漏网之鱼 → 🚀 节点选择'], ['direct', '🐟 漏网之鱼 → 直连']], (v) => {
          cfg.final = v;
        }),
      ),
      h('span', { class: 'ord-arrows' }),
    ),
  );

  const recommended = recommendedOrder(cfg).filter((id) => !PINNED_TAIL.includes(id));
  const orderChanged = JSON.stringify(movable) !== JSON.stringify(recommended);

  return h(
    'div',
    {},
    h('p', { class: 'muted' }, '从上到下依次匹配,命中即停止。拖拽或用 ↑↓ 调整顺序,右侧选择命中后的去向。'),
    h(
      'div',
      { class: 'toolbar' },
      h('span', { class: 'muted' }, '自定义域名:'),
      ...CUSTOM_ENTRIES.map((c) => h('button', { type: 'button', class: 'ghost small', onclick: () => openCustomEditor(c.kind) }, `+ ${c.label.replace('自定义', '')}`)),
      h('span', { class: 'spacer' }),
      h(
        'button',
        {
          type: 'button',
          class: 'ghost small',
          disabled: !orderChanged,
          onclick: () => {
            cfg.order = [];
            renderPanel();
            refreshChrome();
          },
        },
        '恢复推荐顺序',
      ),
      h(
        'button',
        {
          type: 'button',
          class: 'ghost small',
          disabled: !Object.keys(cfg.targets).length,
          onclick: () => {
            cfg.targets = {};
            renderPanel();
            refreshChrome();
          },
        },
        '恢复推荐去向',
      ),
    ),
    customEditor ? renderCustomEditor(customEditor) : null,
    order.length ? null : h('p', { class: 'muted' }, '还没有选择规则,回到上一步勾选。'),
    h('ol', { class: 'ord-list' }, ...rows),
  );
}

function openCustomEditor(kind: CustomKind) {
  customEditor = kind;
  renderPanel();
  document.querySelector<HTMLTextAreaElement>('#customInput')?.focus();
}

function renderCustomEditor(kind: CustomKind): HTMLElement {
  const entry = CUSTOM_ENTRIES.find((c) => c.kind === kind)!;
  const ta = h('textarea', { id: 'customInput', rows: 4, spellcheck: false, placeholder: 'example.com\nfoo.example.org', value: customDomains(cfg, kind).join('\n') });
  const apply = () => {
    const list = ta.value.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
    const field = kind === 'reject' ? 'customReject' : kind === 'direct' ? 'customDirect' : 'customProxy';
    try {
      cfg[field] = validateConfig({ ...draftConfig(), [field]: list })[field];
    } catch (e) {
      toast(e instanceof ValidationError ? e.message : '域名格式有误', 'err');
      return;
    }
    customEditor = null;
    renderPanel();
    refreshChrome();
  };
  return h(
    'div',
    { class: 'custom-editor' },
    h('b', {}, `${entry.label} → ${entry.target}`),
    h('p', { class: 'muted' }, '每行一个域名,匹配该域名及其子域名。清空则移除该条目。新条目默认排在最前面,之后可调整。'),
    ta,
    h(
      'div',
      { class: 'btnrow' },
      h('button', { type: 'button', onclick: apply }, '确定'),
      h(
        'button',
        {
          type: 'button',
          class: 'ghost',
          onclick: () => {
            customEditor = null;
            renderPanel();
          },
        },
        '取消',
      ),
    ),
  );
}

// ---------- ④ 策略组 ----------

function groupOutbounds(kind: GroupKind): Outbound[] {
  const regions = REGIONS.filter((r) => cfg.regions.includes(r.key)).flatMap((r): Outbound[] => (cfg.regionType === 'both' ? [r.key, `${r.key}:auto`] : [r.key]));
  const airs: Outbound[] = multi() ? cfg.upstreams.map((_, i): Outbound => `air:${i}`) : [];
  if (kind === 'reject') return ['reject', 'direct'];
  if (kind === 'direct') return ['direct', 'proxy', ...regions, ...airs];
  return ['proxy', 'auto', ...regions, ...airs, 'direct', 'reject'];
}

function probeTotals(): { stats: NodeStats; providerCount: number } | null {
  const p = probeValid();
  if (!p) return null;
  const stats: NodeStats = { total: 0, info: 0, regions: {}, unmatched: 0 };
  let providerCount = 0;
  for (const r of p.results) {
    if (!r.stats) {
      providerCount++;
      continue;
    }
    stats.total += r.stats.total;
    stats.unmatched += r.stats.unmatched;
    for (const [k, v] of Object.entries(r.stats.regions)) stats.regions[k as RegionKey] = (stats.regions[k as RegionKey] ?? 0) + (v ?? 0);
  }
  return { stats, providerCount };
}

function renderGroupsStep(): HTMLElement {
  const totals = probeTotals();
  let banner: HTMLElement;
  if (totals && totals.stats.total > 0) {
    const detected = REGIONS.filter((r) => totals.stats.regions[r.key]).map((r) => r.key);
    const mostlyUnmatched = totals.stats.unmatched >= totals.stats.total / 2;
    banner = h(
      'div',
      { class: 'banner' },
      h('div', {}, h('b', {}, '检测结果:'), `${totals.stats.total} 个节点,${regionSummary(totals.stats)}`),
      totals.providerCount ? h('div', { class: 'muted' }, `另有 ${totals.providerCount} 个机场由客户端拉取,无法统计。`) : null,
      mostlyUnmatched ? h('div', {}, '多数节点名没有地区信息,建议少选地区并开启「平铺节点」。') : null,
      h(
        'button',
        {
          type: 'button',
          class: 'small',
          onclick: () => {
            cfg.regions = detected;
            if (mostlyUnmatched) cfg.flatNodes = true;
            pruneRefs();
            renderPanel();
            refreshChrome();
            toast('已按检测结果设置');
          },
        },
        '按检测结果设置',
      ),
    );
  } else {
    banner = h('div', { class: 'banner muted' }, '提示:在第 1 步「检测订阅」后,这里会按节点实际分布推荐地区。');
  }

  const labels = new Map(outboundOptions().map(([o, l]) => [o as string, l]));
  const services = planServiceGroups(cfg);
  const defaults = services.map((s) => {
    const opts = groupOutbounds(s.kind);
    const cur = cfg.groupDefaults[s.id];
    return h(
      'div',
      { class: 'row' },
      h('span', { class: 'row-label' }, s.name),
      select<Outbound>(cur && opts.includes(cur) ? cur : opts[0], opts.map((o) => [o, labels.get(o) ?? o]), (v) => {
        if (v === opts[0]) delete cfg.groupDefaults[s.id];
        else cfg.groupDefaults[s.id] = v;
        renderPanel();
      }),
    );
  });

  return h(
    'div',
    { class: 'groups-layout' },
    h(
      'div',
      {},
      banner,
      h('h3', {}, '地区分组'),
      h(
        'div',
        { class: 'chips' },
        ...REGIONS.map((r) =>
          checkbox(
            cfg.regions.includes(r.key),
            r.name,
            (on) => {
              cfg.regions = on ? REGIONS.filter((x) => x.key === r.key || cfg.regions.includes(x.key)).map((x) => x.key) : cfg.regions.filter((x) => x !== r.key);
              pruneRefs();
              renderPanel();
              refreshChrome();
            },
            totals ? `${totals.stats.regions[r.key] ?? 0} 个节点` : undefined,
          ),
        ),
      ),
      h(
        'div',
        { class: 'field' },
        h('span', {}, '地区组类型'),
        select(cfg.regionType, [['url-test', '自动测速选最快'], ['select', '手动选择'], ['both', '两者都生成(手动 + 自动)']], (v) => {
          cfg.regionType = v;
          pruneRefs();
          renderPanel();
        }),
      ),
      h(
        'div',
        { class: 'field' },
        h('span', {}, '节点平铺'),
        checkbox(cfg.flatNodes, '服务策略组平铺全部节点', (on) => {
          cfg.flatNodes = on;
          renderPanel();
        }, '适合节点少、节点名没有地区的机场;🚀 节点选择始终平铺'),
      ),
      h('h3', {}, '各组默认选中'),
      h('p', { class: 'muted' }, '客户端首次导入时默认选中的一项,之后可在客户端随时切换,并会被记住。'),
      h('div', { class: 'rows' }, ...(defaults.length ? defaults : [h('p', { class: 'muted' }, '当前没有规则指向服务策略组。')])),
    ),
    renderGroupPreview(),
  );
}

function renderGroupPreview(): HTMLElement {
  const out = generateConfig(draftConfig(), cfg.upstreams.map(() => ({ kind: 'provider' })), { origin: location.origin });
  const groups = out['proxy-groups'] as { name: string; type: string; proxies?: string[]; use?: string[] }[];
  const typeLabel: Record<string, string> = { select: '手动', 'url-test': '测速' };
  return h(
    'aside',
    { class: 'preview-tree' },
    h('h3', {}, `客户端将看到 ${groups.length} 个策略组`),
    h(
      'ul',
      {},
      ...groups.map((g) => {
        const opts = [...(g.proxies ?? []), ...(g.use?.length ? ['机场节点…'] : [])];
        const shown = opts.slice(0, 4).join('、') + (opts.length > 4 ? ` 等 ${opts.length} 项` : '');
        return h('li', {}, h('b', {}, g.name), h('span', { class: 'tag' }, typeLabel[g.type] ?? g.type), h('small', {}, shown));
      }),
    ),
  );
}

// ---------- ⑤ 完成与测试 ----------

function renderFinishStep(): HTMLElement {
  const toggle = (key: 'ipv6' | 'allowLan' | 'sniffer', label: string) =>
    checkbox(cfg[key], label, (on) => {
      cfg[key] = on;
    });

  const advanced = h(
    'details',
    { class: 'advanced' },
    h('summary', {}, '高级设置(DNS、节点获取方式、规则镜像等,通常不用改)'),
    h('h3', {}, 'DNS'),
    h(
      'div',
      { class: 'radios' },
      ...DNS_PRESETS.map((d) =>
        h(
          'label',
          { class: 'radio' },
          h('input', { type: 'radio', name: 'dns', checked: cfg.dns === d.key, onchange: () => (cfg.dns = d.key) }),
          h('span', {}, h('b', {}, d.label), h('small', {}, d.desc)),
        ),
      ),
    ),
    h('div', { class: 'chips' }, toggle('sniffer', '域名嗅探'), toggle('ipv6', 'IPv6'), toggle('allowLan', '允许局域网连接')),
    h(
      'label',
      { class: 'field' },
      h('span', {}, '节点获取方式'),
      select(
        cfg.mode,
        [
          ['auto', '自动:能解析就内联节点,否则交给客户端拉取'],
          ['inline', '内联:由本站拉取并写入节点'],
          ['provider', 'Provider:客户端直接拉取机场订阅'],
        ],
        (v) => (cfg.mode = v),
      ),
    ),
    h('label', { class: 'field' }, h('span', {}, '拉取订阅的 User-Agent'), h('input', { value: cfg.ua, maxLength: 200, spellcheck: false, oninput: (e: Event) => (cfg.ua = (e.target as HTMLInputElement).value) })),
    h('label', { class: 'field' }, h('span', {}, '规则文件镜像'), select(cfg.mirror, MIRRORS.map((m) => [m.key, m.label]), (v) => (cfg.mirror = v))),
  );

  return h(
    'div',
    {},
    h('label', { class: 'field' }, h('span', {}, '配置名称'), h('input', { value: cfg.name, maxLength: 40, oninput: (e: Event) => (cfg.name = (e.target as HTMLInputElement).value) })),
    advanced,
    h(
      'div',
      { class: 'btnrow' },
      h('button', { type: 'button', class: 'ghost', onclick: preview }, '预览 YAML'),
      current ? h('button', { type: 'button', class: 'ghost', onclick: () => void save(true) }, '另存为新订阅') : null,
      h('button', { type: 'button', onclick: () => void save(false) }, current ? '保存修改' : '生成订阅链接'),
    ),
    renderResult(),
    renderTester(),
  );
}

function renderTester(): HTMLElement {
  const input = h('input', {
    type: 'text',
    value: testInput,
    placeholder: '输入网址、域名或 IP,如 https://www.netflix.com',
    spellcheck: false,
    oninput: (e: Event) => (testInput = (e.target as HTMLInputElement).value),
    onkeydown: (e: Event) => {
      if ((e as KeyboardEvent).key === 'Enter') void doTest();
    },
  });
  const samples = ['chatgpt.com', 'www.netflix.com', 'github.com', 'www.baidu.com', 'doubleclick.net'];
  return h(
    'section',
    { class: 'tester' },
    h('h3', {}, '流量测试(可选)'),
    h('p', { class: 'muted' }, '按当前配置(无需先保存)在浏览器里逐条模拟匹配,使用与客户端相同的规则内容。'),
    h('div', { class: 'copyrow' }, input, h('button', { type: 'button', disabled: testState.running, onclick: () => void doTest() }, testState.running ? '测试中…' : '测试')),
    h(
      'div',
      { class: 'samples' },
      h('span', { class: 'muted' }, '试试:'),
      ...samples.map((s) =>
        h(
          'button',
          {
            type: 'button',
            class: 'link',
            onclick: () => {
              testInput = s;
              void doTest();
            },
          },
          s,
        ),
      ),
    ),
    testState.running ? h('p', { id: 'testProgress', class: 'muted' }, testState.progress ?? '') : null,
    testState.error ? h('p', { class: 'error' }, testState.error) : null,
    testState.result ? renderTestResult(testState.result) : null,
  );
}

function renderTestResult(r: TestResult): HTMLElement {
  const hit = r.hit;
  const skipped = r.rows.filter((x) => x.status !== 'hit');
  const noResolve = skipped.filter((x) => x.status === 'skip-no-resolve').length;
  const isMatch = hit?.rule.startsWith('MATCH');
  const statusText: Record<string, string> = { hit: '✓ 命中', miss: '未命中', 'skip-no-resolve': '跳过:no-resolve', 'skip-type': '跳过:类型不适用' };
  return h(
    'div',
    { class: 'test-result' },
    h('div', { class: 'test-host' }, h('b', {}, r.target.host), r.resolveFailed
        ? h('small', { class: 'error' }, 'DNS 解析失败(DoH 不可用),IP 规则按未命中处理')
        : r.resolved
          ? h('small', { class: 'muted' }, `解析为 ${r.resolved.slice(0, 3).join('、') || '(无记录)'}`)
          : null),
    hit
      ? h(
          'div',
          { class: 'test-chain' },
          h('div', {}, h('span', { class: 'tag' }, '命中'), isMatch ? '没有规则命中,走 MATCH' : `第 ${hit.index} 条  ${hit.rule}`),
          hit.matched && !isMatch ? h('div', { class: 'muted' }, `匹配项:${hit.matched}`) : null,
          h('div', { class: 'chain' }, ...r.chain.flatMap((c, i) => (i ? [h('span', { class: 'arrow' }, '→'), h('span', { class: 'node' }, c)] : [h('span', { class: 'node' }, c)]))),
          h('div', { class: 'muted' }, `${r.chainNote ? `${r.chainNote}。` : ''}按各组默认选中推演;客户端里手动切换过的,以实际选择为准。`),
        )
      : h('div', { class: 'muted' }, '没有命中'),
    h(
      'details',
      {},
      h('summary', {}, `查看匹配过程(跳过 ${skipped.length} 条${noResolve ? `,其中 ${noResolve} 条 IP 规则因 no-resolve 不解析域名` : ''})`),
      h('ol', { class: 'trace' }, ...r.rows.map((x) => h('li', { class: x.status }, h('span', { class: 'mono' }, x.rule), h('small', {}, statusText[x.status])))),
    ),
  );
}

async function doTest() {
  if (!testInput.trim()) {
    toast('请输入要测试的网址', 'err');
    return;
  }
  testState = { running: true, progress: '准备中…' };
  renderPanel();
  try {
    const result = await runTest(testInput, cfg, location.origin, (msg) => {
      testState.progress = msg;
      const p = document.querySelector('#testProgress');
      if (p) p.textContent = msg;
    });
    testState = { running: false, result };
  } catch (e) {
    testState = { running: false, error: e instanceof Error ? e.message : '测试失败' };
  }
  if (step === STEPS.length - 1) renderPanel();
}

function renderResult(): HTMLElement | null {
  if (!current) return null;
  const url = `${location.origin}/sub/${current.id}`;
  const manage = `${location.origin}/#manage=${current.id}.${current.token}`;
  const importUrl = `clash://install-config?url=${encodeURIComponent(url)}&name=${encodeURIComponent(current.name)}`;
  const confirmBox = h('span', { class: 'confirm', hidden: true });
  confirmBox.append(
    '确定删除?订阅链接将立即失效 ',
    h('button', { type: 'button', class: 'danger', onclick: () => void removeCurrent() }, '确认删除'),
    h('button', { type: 'button', class: 'ghost', onclick: () => (confirmBox.hidden = true) }, '取消'),
  );
  return h(
    'section',
    { class: 'result' },
    h('h3', {}, '订阅链接'),
    h('div', { class: 'copyrow' }, h('input', { readOnly: true, value: url }), h('button', { type: 'button', onclick: () => copy(url) }, '复制')),
    h('div', { class: 'btnrow' }, h('a', { class: 'button ghost', href: importUrl }, '一键导入 Clash / Mihomo 客户端')),
    h(
      'details',
      {},
      h('summary', {}, '管理链接与删除'),
      h('p', { class: 'muted' }, '管理链接可在其他浏览器打开并编辑此订阅(仍需管理员口令)。'),
      h('div', { class: 'copyrow' }, h('input', { readOnly: true, value: manage }), h('button', { type: 'button', onclick: () => copy(manage) }, '复制')),
      h('div', { class: 'btnrow' }, h('button', { type: 'button', class: 'danger ghost', onclick: () => (confirmBox.hidden = false) }, '删除此订阅'), confirmBox),
    ),
  );
}

// ---------- 订阅管理 ----------

function copy(text: string) {
  navigator.clipboard.writeText(text).then(
    () => toast('已复制'),
    () => toast('复制失败,请手动选择复制', 'err'),
  );
}

function renderSaved() {
  const list = loadSaved();
  $('#saved').replaceChildren(
    ...(list.length ? [h('span', { class: 'muted' }, '我的订阅:')] : []),
    ...list.map((s) => h('button', { type: 'button', class: `link${current?.id === s.id ? ' active' : ''}`, onclick: () => void openSub(s) }, s.name || s.id.slice(0, 6))),
    h('button', { type: 'button', class: 'link', onclick: newSub }, '+ 新建'),
    h('span', { class: 'spacer' }),
    h('button', { type: 'button', class: 'link', onclick: showLogin }, '退出登录'),
  );
}

async function api(path: string, init: RequestInit & { token?: string } = {}) {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (init.token) headers.authorization = `Bearer ${init.token}`;
  if (adminToken) headers['x-admin-token'] = adminToken;
  const res = await fetch(path, { ...init, headers });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.status === 401) {
    showLogin();
    throw new Error(typeof data.error === 'string' ? data.error : '需要管理员口令');
  }
  if (!res.ok) throw new Error(typeof data.error === 'string' ? data.error : `请求失败 (${res.status})`);
  return data;
}

function resetTransient() {
  probe = null;
  customEditor = null;
  testState = { running: false };
}

async function openSub(s: SavedSub) {
  try {
    const data = await api(`/api/configs/${encodeURIComponent(s.id)}`, { token: s.token });
    cfg = { ...defaultConfig(), ...(data.config as UserConfig) };
    current = { ...s, name: cfg.name };
    upsertSaved(current);
    resetTransient();
    step = STEPS.length - 1;
    render();
  } catch (e) {
    toast(e instanceof Error ? e.message : '加载失败', 'err');
  }
}

function newSub() {
  cfg = defaultConfig();
  current = null;
  resetTransient();
  step = 0;
  render();
}

async function save(asNew: boolean) {
  let v: UserConfig;
  try {
    v = validateConfig({ ...cfg, upstreams: cfg.upstreams.map((u) => ({ name: u.name.trim(), url: u.url.trim() })), order: effectiveOrder(cfg) });
  } catch (e) {
    toast(e instanceof ValidationError ? e.message : '配置有误', 'err');
    if (e instanceof ValidationError && /订阅|机场/.test(e.message)) {
      step = 0;
      render();
    }
    return;
  }
  const btns = [...document.querySelectorAll<HTMLButtonElement>('#barInner button, #panel button')];
  btns.forEach((b) => (b.disabled = true));
  try {
    if (current && !asNew) {
      await api(`/api/configs/${encodeURIComponent(current.id)}`, { method: 'PUT', token: current.token, body: JSON.stringify({ config: v }) });
      current = { ...current, name: v.name };
      toast('已保存,客户端更新订阅即可生效');
    } else {
      const data = await api('/api/configs', { method: 'POST', body: JSON.stringify({ config: v }) });
      current = { id: String(data.id), token: String(data.token), name: v.name };
      toast('订阅已生成');
    }
    cfg = v;
    upsertSaved(current);
    step = STEPS.length - 1;
    render();
    document.querySelector('.result')?.scrollIntoView({ behavior: 'smooth' });
  } catch (e) {
    toast(e instanceof Error ? e.message : '保存失败', 'err');
    btns.forEach((b) => (b.disabled = false));
  }
}

async function removeCurrent() {
  if (!current) return;
  try {
    await api(`/api/configs/${encodeURIComponent(current.id)}`, { method: 'DELETE', token: current.token });
    saveSaved(loadSaved().filter((s) => s.id !== current!.id));
    toast('已删除');
    newSub();
  } catch (e) {
    toast(e instanceof Error ? e.message : '删除失败', 'err');
  }
}

function preview() {
  const yaml = toYaml(generateConfig(draftConfig(), cfg.upstreams.map(() => ({ kind: 'provider' })), { origin: location.origin }));
  $('#previewCode').textContent = yaml;
  $<HTMLDialogElement>('#previewDialog').showModal();
}

// ---------- 登录与启动 ----------

function setAuthed(on: boolean) {
  $('#login').hidden = on;
  $('#app').hidden = !on;
  $('#bar').hidden = !on;
  $('#saved').hidden = !on;
}

function showLogin() {
  adminToken = null;
  storeAdmin(null);
  setAuthed(false);
  $<HTMLInputElement>('#adminToken').focus();
}

let started = false;

async function login(token: string, remember: boolean): Promise<boolean> {
  adminToken = token;
  try {
    await api('/api/session');
  } catch (e) {
    adminToken = null;
    setAuthed(false);
    toast(e instanceof Error ? e.message : '登录失败', 'err');
    return false;
  }
  storeAdmin(token, remember);
  setAuthed(true);
  if (!started) {
    started = true;
    start();
  }
  return true;
}

function start() {
  $('#closePreview').addEventListener('click', () => $<HTMLDialogElement>('#previewDialog').close());
  $('#copyPreview').addEventListener('click', () => copy($('#previewCode').textContent ?? ''));
  render();
  const m = /^#manage=([A-Za-z0-9_-]{22})\.([A-Za-z0-9_-]{32})$/.exec(location.hash);
  if (m) {
    history.replaceState(null, '', location.pathname);
    void openSub({ id: m[1], token: m[2], name: '' });
  } else {
    const last = loadSaved()[0];
    if (last) void openSub(last);
  }
}

function init() {
  $('#loginForm').addEventListener('submit', (e) => {
    e.preventDefault();
    const input = $<HTMLInputElement>('#adminToken');
    void login(input.value, $<HTMLInputElement>('#remember').checked).then((ok) => {
      if (ok) input.value = '';
    });
  });
  if (adminToken) void login(adminToken, lsGet('local', ADMIN_KEY) !== null);
  else setAuthed(false);
}

init();
